/**
 * @fileoverview Estilos da tela "Ordens de serviço". Monocromática, no padrão
 * visual de /integrations; cor só com significado: o coletor parado e o estado
 * da triagem de cada OS (nos tons do painel da triagem no chat).
 */

import { makeStyles } from "@material-ui/core/styles";
import { TONES, tint, tokens } from "../../components/Triage/styles";

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const tone = tokens(isDark);
  const textPrimary = isDark ? "#f0f2f5" : "#111318";
  const textSecondary = isDark ? "#a0aec0" : "#4b5563";
  const textFaint = isDark ? "#718096" : "#6b7280";
  const border = isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.12)";
  const inputBorder = isDark ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.18)";
  const surface = isDark ? "rgba(17, 19, 24, 0.85)" : "#ffffff";
  const rowHover = isDark ? "rgba(255,255,255,0.035)" : "rgba(15,23,42,0.035)";
  const chipBg = isDark ? "rgba(148,163,184,0.14)" : "rgba(71,85,105,0.08)";
  const chipText = isDark ? "#cbd5e1" : "#334155";

  return {
    page: {
      padding: "28px 32px 40px",
      minHeight: "100%",
      fontFamily: "'Montserrat', sans-serif",
      "& *": { fontFamily: "'Montserrat', sans-serif" },
      [theme.breakpoints.down("sm")]: { padding: "20px 16px 32px" },
    },
    header: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 16,
      marginBottom: 20,
      flexWrap: "wrap",
    },
    title: {
      fontSize: 22,
      fontWeight: 600,
      color: textPrimary,
      display: "flex",
      alignItems: "center",
      gap: 10,
    },
    subtitle: { fontSize: 12, color: textSecondary, marginTop: 4 },
    linkBtn: {
      height: 34,
      padding: "0 12px",
      borderRadius: 8,
      border: `1px solid ${inputBorder}`,
      background: "transparent",
      color: textPrimary,
      fontSize: 12,
      fontWeight: 500,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      "&:hover": { background: rowHover },
    },

    // Estado do coletor
    sourceLine: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 12,
      color: textSecondary,
      marginBottom: 8,
    },
    dotOk: { width: 8, height: 8, borderRadius: 4, background: isDark ? "#22c55e" : "#15803d", flexShrink: 0 },
    dotOff: { width: 8, height: 8, borderRadius: 4, background: textFaint, flexShrink: 0 },
    dotError: { width: 8, height: 8, borderRadius: 4, background: isDark ? "#ef4444" : "#b91c1c", flexShrink: 0 },
    // Opção do filtro de fonte: ponto do estado do coletor e o nome.
    sourceOption: { display: "inline-flex", alignItems: "center", gap: 8 },
    banner: {
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
      padding: "12px 14px",
      borderRadius: 10,
      fontSize: 13,
      lineHeight: 1.45,
      marginBottom: 10,
      border: "1px solid",
    },
    bannerWarn: {
      background: isDark ? "rgba(245,158,11,0.08)" : "#fffbeb",
      borderColor: isDark ? "rgba(245,158,11,0.35)" : "#f59e0b",
      color: isDark ? "#fcd34d" : "#78350f",
    },
    bannerError: {
      background: isDark ? "rgba(239,68,68,0.08)" : "#fef2f2",
      borderColor: isDark ? "rgba(239,68,68,0.35)" : "#ef4444",
      color: isDark ? "#fca5a5" : "#7f1d1d",
    },
    bannerNeutral: {
      background: isDark ? "rgba(148,163,184,0.08)" : "#f8fafc",
      borderColor: border,
      color: textSecondary,
    },
    // Aviso de OS novas com várias páginas abertas: fica visível enquanto a lista rola.
    bannerSticky: {
      position: "sticky",
      top: 8,
      zIndex: 2,
      alignItems: "center",
      boxShadow: isDark ? "0 6px 20px rgba(0,0,0,0.45)" : "0 6px 20px rgba(15,23,42,0.10)",
      background: isDark ? "#1b1f27" : "#ffffff",
    },

    // Filtros
    filters: {
      display: "flex",
      gap: 10,
      alignItems: "center",
      flexWrap: "wrap",
      margin: "16px 0 12px",
    },
    searchWrap: { position: "relative", flex: "1 1 280px", maxWidth: 420 },
    searchIcon: {
      position: "absolute",
      left: 12,
      top: "50%",
      transform: "translateY(-50%)",
      color: textFaint,
    },
    // Busca com a mesma cara dos selects ao lado (ModernSelect: canto, borda e fundo).
    input: {
      width: "100%",
      height: 40,
      padding: "0 12px 0 36px",
      fontSize: 13,
      borderRadius: 14,
      border: `1px solid ${isDark ? "rgba(91, 124, 255, 0.15)" : "rgba(15, 23, 42, 0.2)"}`,
      background: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
      color: textPrimary,
      outline: "none",
      transition: "border-color 0.2s, box-shadow 0.2s",
      "&:hover": { borderColor: isDark ? "rgba(91, 124, 255, 0.25)" : "rgba(61, 92, 253, 0.4)" },
      "&:focus": {
        borderColor: isDark ? "rgba(91, 124, 255, 0.4)" : "rgba(61, 92, 253, 0.6)",
        boxShadow: "0 0 0 1px rgba(91, 124, 255, 0.15)",
      },
      "&::placeholder": { color: textFaint },
    },
    // Filtros com o select padrão do app (ModernSelect), do tamanho da busca.
    filterSelect: {
      minWidth: 190,
      "& .MuiSelect-select": { fontSize: 13 },
    },
    total: { fontSize: 12, color: textSecondary, marginLeft: "auto" },

    // Barra do estado da triagem: quantas OS há em cada um; um clique filtra.
    stateBar: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", margin: "0 0 12px" },
    stateBarLabel: {
      fontSize: 11,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: 0.4,
      color: textSecondary,
      marginRight: 4,
    },
    stateChip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 7,
      height: 30,
      padding: "0 12px",
      borderRadius: 999,
      border: `1px solid ${inputBorder}`,
      background: "transparent",
      color: textSecondary,
      fontSize: 12,
      fontWeight: 500,
      cursor: "pointer",
      whiteSpace: "nowrap",
      "&:hover": { background: rowHover, color: textPrimary },
      "&:focus-visible": { outline: `2px solid ${textPrimary}`, outlineOffset: 2 },
    },
    stateChipOn: {
      borderColor: textPrimary,
      background: chipBg,
      color: textPrimary,
      fontWeight: 600,
      "& $stateCount": { color: textSecondary },
    },
    stateCount: { fontWeight: 600, color: textFaint, fontVariantNumeric: "tabular-nums" },
    stateDot: { width: 8, height: 8, borderRadius: 4, flexShrink: 0 },

    // Tabela
    tableWrap: {
      background: surface,
      border: `1px solid ${border}`,
      borderRadius: 12,
      overflowX: "auto",
    },
    table: { width: "100%", borderCollapse: "collapse", minWidth: 760 },
    th: {
      textAlign: "left",
      fontSize: 11,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: 0.4,
      color: textSecondary,
      // 12px dos lados (th e td): as 9 colunas cabem em 1280 px sem rolar de lado.
      padding: "10px 12px",
      borderBottom: `1px solid ${border}`,
      whiteSpace: "nowrap",
    },
    // Título que ordena: a seta aparece na coluna escolhida (e ao passar o mouse).
    thBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: 0,
      border: "none",
      background: "transparent",
      color: "inherit",
      font: "inherit",
      textTransform: "inherit",
      letterSpacing: "inherit",
      cursor: "pointer",
      "&:hover": { color: textPrimary },
      "&:hover $thIcon": { opacity: 0.55 },
      "&:focus-visible": { outline: `2px solid ${textPrimary}`, outlineOffset: 2, borderRadius: 4 },
      "&:focus-visible $thIcon": { opacity: 0.55 },
    },
    thBtnOn: { color: textPrimary, "& $thIcon, &:hover $thIcon": { opacity: 1 } },
    thIcon: { opacity: 0, flexShrink: 0, transition: "opacity 120ms" },
    tr: {
      cursor: "pointer",
      "&:hover": { background: rowHover },
      "&:not(:last-child) td": { borderBottom: `1px solid ${border}` },
      "&:focus-visible": { outline: "2px solid #4f7cff", outlineOffset: -2 },
    },
    td: { fontSize: 13, color: textPrimary, padding: "11px 12px", verticalAlign: "top" },
    tdMuted: { fontSize: 12, color: textSecondary },
    osNumber: { fontWeight: 600, whiteSpace: "nowrap" },
    chip: {
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: 10,
      fontSize: 11,
      fontWeight: 500,
      background: chipBg,
      color: chipText,
      whiteSpace: "nowrap",
      // Mesma altura com ou sem a borda do tom.
      border: "1px solid transparent",
    },
    // Estado da triagem: o cinza é o chip comum; os outros ganham o tom.
    triageChip: { fontWeight: 600 },
    ...Object.fromEntries(
      TONES.filter((t) => t !== "neutral").map((t) => [
        `pill_${t}`,
        {
          color: tone[t],
          background: tint(tone[t], isDark ? 0.14 : 0.08),
          borderColor: tint(tone[t], isDark ? 0.4 : 0.3),
        },
      ])
    ),
    ...Object.fromEntries(TONES.map((t) => [`dot_${t}`, { background: tone[t] }])),
    empty: {
      padding: "56px 20px",
      textAlign: "center",
      color: textSecondary,
      fontSize: 13,
      border: `1px dashed ${border}`,
      borderRadius: 12,
    },
    emptyTitle: { fontWeight: 600, color: textPrimary, marginBottom: 4, fontSize: 14 },
    loadMore: { display: "flex", justifyContent: "center", marginTop: 14 },

    // Celular: um cartão por OS no lugar da tabela
    cardList: { display: "flex", flexDirection: "column", gap: 8 },
    mCard: {
      display: "block",
      width: "100%",
      textAlign: "left",
      background: surface,
      border: `1px solid ${border}`,
      borderRadius: 12,
      padding: "12px 14px",
      color: textPrimary,
      cursor: "pointer",
      "&:active": { background: rowHover },
    },
    mCardTop: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 6 },
    mCardName: { fontSize: 14, fontWeight: 500, color: textPrimary, marginBottom: 2 },

    // Painel de detalhe
    drawerPaper: {
      width: 480,
      maxWidth: "100vw",
      background: isDark ? "#14171d" : "#ffffff",
      color: textPrimary,
      fontFamily: "'Montserrat', sans-serif",
      "& *": { fontFamily: "'Montserrat', sans-serif" },
    },
    drawerHeader: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 12,
      padding: "18px 20px",
      borderBottom: `1px solid ${border}`,
    },
    drawerTitle: { fontSize: 18, fontWeight: 700, color: textPrimary },
    drawerSub: { fontSize: 12, color: textSecondary, marginTop: 4, display: "flex", gap: 6, flexWrap: "wrap" },
    drawerBody: { padding: "8px 20px 28px", overflowY: "auto", flex: 1 },
    section: { padding: "14px 0", "&:not(:last-child)": { borderBottom: `1px solid ${border}` } },
    sectionTitle: {
      fontSize: 11,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: 0.4,
      color: textSecondary,
      marginBottom: 10,
    },
    field: { display: "grid", gridTemplateColumns: "150px 1fr", gap: 10, fontSize: 13, padding: "4px 0" },
    fieldLabel: { color: textSecondary },
    fieldValue: { color: textPrimary, wordBreak: "break-word", whiteSpace: "pre-wrap" },
    longText: {
      fontSize: 13,
      lineHeight: 1.5,
      color: textPrimary,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    },
    // Triagem da OS (seção do painel e diálogo de início)
    primaryBtn: {
      height: 34,
      padding: "0 14px",
      borderRadius: 8,
      border: `1px solid ${textPrimary}`,
      background: textPrimary,
      color: isDark ? "#111318" : "#ffffff",
      fontSize: 12.5,
      fontWeight: 600,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      "&:disabled": { opacity: 0.45, cursor: "default" },
      "&:focus-visible": { outline: `2px solid ${textPrimary}`, outlineOffset: 2 },
    },
    triageRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10,
      padding: "8px 0",
      "&:not(:last-child)": { borderBottom: `1px dashed ${border}` },
    },
    triageMain: { display: "flex", flexDirection: "column", gap: 3, minWidth: 0 },
    triageName: { fontSize: 13, fontWeight: 500, color: textPrimary, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" },
    triageNote: {
      margin: "4px 0 10px",
      padding: "8px 10px",
      borderRadius: 8,
      fontSize: 12,
      lineHeight: 1.45,
      background: chipBg,
      color: chipText,
    },
    triageActions: { display: "flex", justifyContent: "flex-end", marginTop: 10 },
    triageOpen: { whiteSpace: "nowrap", flexShrink: 0 },
    dialogBody: { display: "flex", flexDirection: "column", gap: 14, paddingTop: 4 },
    dialogLabel: { fontSize: 12.5, fontWeight: 600, color: textPrimary, marginBottom: 2 },
    dialogHint: { fontSize: 12, color: textSecondary, lineHeight: 1.45 },
    radioGroup: {
      "& .MuiFormControlLabel-label": { fontSize: 13, color: textPrimary },
      "& .MuiRadio-root": { color: textSecondary, padding: 6 },
    },

    purged: {
      margin: "14px 0",
      padding: "10px 12px",
      borderRadius: 8,
      fontSize: 12,
      background: chipBg,
      color: chipText,
    },
  };
});

export default useStyles;
